import React, { useState, useEffect, useMemo, useRef } from 'react';
import XLSX from 'xlsx-js-style';
import { useNotification } from '../../context/NotificationContext';
import client from '../../services/graphqlClient';
import './InventarioBodega.css';

const formatMoney = (v) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(v || 0);
const formatNum = (v) => new Intl.NumberFormat('es-CO', { maximumFractionDigits: 2 }).format(v || 0);
const formatFecha = (f) => f ? new Date(f + 'T12:00:00-05:00').toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'America/Bogota' }) : '-';
const hoyBogota = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Bogota' });
const sinTildes = (s) => (s || '').toString().normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

const TIPO_KARDEX = {
  entrada: { texto: 'Entrada', clase: 'k-entrada' },
  salida: { texto: 'Salida', clase: 'k-salida' },
  ajuste_entrada: { texto: 'Ajuste (+)', clase: 'k-ajuste' },
  ajuste_salida: { texto: 'Ajuste (−)', clase: 'k-ajuste' },
};

// ----- Excel -----
const ESTILO_TITULO = { font: { bold: true, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: '1F6F3A' } }, alignment: { vertical: 'center', horizontal: 'center', wrapText: true } };
const ESTILO_EDITABLE_TITULO = { ...ESTILO_TITULO, fill: { fgColor: { rgb: 'B45309' } } };
const ESTILO_EDITABLE = { fill: { fgColor: { rgb: 'FEF3C7' } }, font: { bold: true } };
const KARDEX_COLS = ['Artículo', 'Unidad', 'Fecha', 'Tipo', 'Detalle', 'Factura', 'Entrada', 'Salida', 'Saldo', 'Precio unitario', 'Valor movimiento', 'Costo promedio', 'Saldo valorizado', 'Registrado por', 'Observaciones'];

const filaKardex = (art, m) => [
  art.articulo_nombre, art.unidad_medida || '', m.fecha || '', TIPO_KARDEX[m.tipo]?.texto || m.tipo, m.detalle || '', m.numero_factura || '',
  m.entrada || 0, m.salida || 0, m.saldo, m.precio_unitario || 0, m.valor_movimiento || 0, m.costo_promedio || 0, m.saldo_valor || 0,
  m.usuario || '', m.observaciones || '',
];

const hojaKardex = (articulos) => {
  const filas = [KARDEX_COLS];
  articulos.forEach(a => a.movimientos.forEach(m => filas.push(filaKardex(a, m))));
  const ws = XLSX.utils.aoa_to_sheet(filas);
  KARDEX_COLS.forEach((_, c) => { const celda = ws[XLSX.utils.encode_cell({ r: 0, c })]; if (celda) celda.s = ESTILO_TITULO; });
  ws['!cols'] = [40, 10, 12, 12, 40, 14, 10, 10, 10, 15, 16, 15, 16, 20, 50].map(wch => ({ wch }));
  ws['!freeze'] = { xSplit: 0, ySplit: 1 };
  return ws;
};

const InventarioBodega = () => {
  const { success, error, warning } = useNotification();
  const [inventario, setInventario] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [filtro, setFiltro] = useState('todos'); // todos | bajo | agotado
  const [exportando, setExportando] = useState(false);
  const [kardex, setKardex] = useState(null);          // { articulo, cargando }
  const [importe, setImporte] = useState(null);        // { items, resultado, archivo }
  const [aplicando, setAplicando] = useState(false);
  const archivoRef = useRef(null);

  useEffect(() => {
    cargar();
  }, []);

  const cargar = async () => {
    try {
      setLoading(true);
      const data = await client.query(`{
        bodegaInventarioResumen {
          articulo_nombre unidad_medida total_entradas total_salidas
          stock_actual precio_promedio valor_total_stock num_movimientos
          ultima_fecha_entrada ultima_fecha_salida ultimo_proveedor ultimo_proyecto
        }
      }`);
      setInventario(data.bodegaInventarioResumen || []);
    } catch (err) {
      console.error('Error cargando inventario:', err);
    } finally {
      setLoading(false);
    }
  };

  const getEstadoStock = (stock) => {
    if (stock <= 0) return 'agotado';
    if (stock < 5) return 'bajo';
    return 'normal';
  };

  const inventarioFiltrado = useMemo(() => {
    let lista = inventario;
    // "Todos" muestra todo el inventario, incluidos los agotados.
    if (filtro === 'bajo') lista = lista.filter(i => i.stock_actual > 0 && i.stock_actual < 5);
    if (filtro === 'agotado') lista = lista.filter(i => i.stock_actual <= 0);
    if (busqueda.trim()) {
      const q = busqueda.toLowerCase();
      lista = lista.filter(i =>
        (i.articulo_nombre || '').toLowerCase().includes(q) ||
        (i.ultimo_proveedor || '').toLowerCase().includes(q) ||
        (i.ultimo_proyecto || '').toLowerCase().includes(q)
      );
    }
    return lista;
  }, [inventario, filtro, busqueda]);

  const stats = useMemo(() => ({
    total: inventario.length,
    bajo: inventario.filter(i => i.stock_actual > 0 && i.stock_actual < 5).length,
    agotado: inventario.filter(i => i.stock_actual <= 0).length,
    valor: inventario.reduce((s, i) => s + (i.valor_total_stock || 0), 0),
  }), [inventario]);

  const consultaKardex = (articulo) => client.query(
    `query($a: String) { bodegaKardex(articulo_nombre: $a) {
      articulo_nombre unidad_medida total_entradas total_salidas stock_actual valor_stock
      movimientos { id fecha tipo detalle numero_factura entrada salida saldo precio_unitario valor_movimiento costo_promedio saldo_valor usuario observaciones }
    } }`, { a: articulo || null });

  // ---------- Exportar: inventario completo + kárdex de todos los artículos ----------
  const exportarExcel = async () => {
    if (!inventario.length) { warning('No hay artículos para exportar'); return; }
    try {
      setExportando(true);
      const k = await consultaKardex(null);

      const titulos = ['Artículo', 'Unidad', 'Entradas', 'Salidas', 'Stock sistema', 'Stock físico', 'Diferencia', 'Precio promedio', 'Valor stock', 'Última entrada', 'Último proveedor', 'Último destino', 'Estado', 'Observación del ajuste'];
      const filas = [titulos];
      inventario.forEach((i, n) => {
        const f = n + 2;
        filas.push([
          i.articulo_nombre, i.unidad_medida || 'unidad', i.total_entradas || 0, i.total_salidas || 0, i.stock_actual || 0,
          i.stock_actual || 0, { f: `F${f}-E${f}` }, Math.round(i.precio_promedio || 0), Math.round(i.valor_total_stock || 0),
          i.ultima_fecha_entrada || '', i.ultimo_proveedor || '', i.ultimo_proyecto || '',
          { agotado: 'AGOTADO', bajo: 'BAJO', normal: 'OK' }[getEstadoStock(i.stock_actual)], '',
        ]);
      });
      const ws = XLSX.utils.aoa_to_sheet(filas);
      const EDITABLES = [5, 13];   // Stock físico y Observación del ajuste
      titulos.forEach((_, c) => { ws[XLSX.utils.encode_cell({ r: 0, c })].s = EDITABLES.includes(c) ? ESTILO_EDITABLE_TITULO : ESTILO_TITULO; });
      for (let r = 1; r < filas.length; r++) {
        EDITABLES.forEach(c => {
          const ref = XLSX.utils.encode_cell({ r, c });
          if (!ws[ref]) ws[ref] = { t: 's', v: '' };
          ws[ref].s = ESTILO_EDITABLE;
        });
      }
      ws['!cols'] = [44, 10, 10, 10, 13, 13, 11, 15, 15, 14, 28, 34, 10, 44].map(wch => ({ wch }));
      ws['!freeze'] = { xSplit: 1, ySplit: 1 };

      const ayuda = XLSX.utils.aoa_to_sheet([
        ['INVENTARIO DE BODEGA · ' + hoyBogota()],
        [''],
        ['Cómo usar este archivo'],
        ['1. En la hoja "Inventario" solo se editan las columnas naranja: "Stock físico" y "Observación del ajuste".'],
        ['2. "Stock físico" viene igual al stock del sistema. Cambia solo los artículos donde el conteo real sea distinto.'],
        ['3. Guarda el archivo y usa el botón "Importar Excel" en el Inventario de Bodega.'],
        ['4. El sistema muestra primero qué va a cambiar. Nada se aplica hasta que confirmes.'],
        [''],
        ['Qué pasa al importar'],
        ['- Por cada artículo con diferencia se registra un movimiento de AJUSTE en el kárdex: entrada si hay más, salida si hay menos.'],
        ['- El ajuste usa el precio promedio del artículo, así que no cambia el precio promedio.'],
        ['- Un ajuste no se carga a ningún proyecto.'],
        ['- Para agregar un artículo que no existe, escribe una fila nueva con Artículo, Unidad, Stock físico y Precio promedio.'],
        ['- No cambies el nombre de los artículos: el sistema los reconoce por el nombre.'],
        ['- Las demás columnas son informativas. Cambiarlas no tiene efecto.'],
        [''],
        ['La hoja "Kárdex" trae todos los movimientos de cada artículo con su saldo. Es de consulta.'],
      ]);
      ayuda['!cols'] = [{ wch: 120 }];
      ayuda.A1.s = { font: { bold: true, sz: 14 } };
      ['A3', 'A9'].forEach(c => { ayuda[c].s = { font: { bold: true } }; });

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Inventario');
      XLSX.utils.book_append_sheet(wb, hojaKardex(k.bodegaKardex || []), 'Kárdex');
      XLSX.utils.book_append_sheet(wb, ayuda, 'Instrucciones');
      XLSX.writeFile(wb, `Inventario_Bodega_${hoyBogota()}.xlsx`);
      success('Inventario exportado');
    } catch (err) {
      console.error('Error exportando:', err);
      error('No se pudo exportar el inventario');
    } finally {
      setExportando(false);
    }
  };

  // ---------- Importar: lee el conteo físico y muestra qué cambiaría ----------
  const leerArchivo = async (e) => {
    const archivo = e.target.files?.[0];
    if (archivoRef.current) archivoRef.current.value = '';
    if (!archivo) return;
    try {
      const wb = XLSX.read(await archivo.arrayBuffer(), { type: 'array' });
      const nombreHoja = wb.SheetNames.find(n => sinTildes(n) === 'inventario') || wb.SheetNames[0];
      const filas = XLSX.utils.sheet_to_json(wb.Sheets[nombreHoja], { header: 1, defval: '' });
      if (filas.length < 2) { error('El archivo no tiene filas'); return; }

      const titulos = filas[0].map(sinTildes);
      const col = (...nombres) => titulos.findIndex(t => nombres.includes(t));
      const cArt = col('articulo', 'articulo_nombre'), cUni = col('unidad', 'unidad de medida', 'unidad_medida');
      const cFis = col('stock fisico', 'stock_fisico', 'conteo fisico', 'conteo'), cPre = col('precio promedio', 'precio unitario', 'precio');
      const cObs = col('observacion del ajuste', 'observacion', 'observaciones');
      if (cArt < 0 || cFis < 0) { error('El archivo debe tener las columnas "Artículo" y "Stock físico"'); return; }

      const aNumero = (v) => {
        if (v === '' || v === null || v === undefined) return null;
        if (typeof v === 'number') return v;
        const n = parseFloat(String(v).replace(/\s/g, '').replace(/\.(?=\d{3}(\D|$))/g, '').replace(',', '.'));
        return Number.isNaN(n) ? null : n;
      };
      const items = [];
      for (let r = 1; r < filas.length; r++) {
        const f = filas[r];
        const nombre = String(f[cArt] ?? '').trim();
        if (!nombre) continue;
        items.push({
          fila: r + 1, articulo_nombre: nombre,
          unidad_medida: cUni >= 0 ? String(f[cUni] ?? '').trim() || null : null,
          stock_fisico: aNumero(f[cFis]),
          precio_unitario: cPre >= 0 ? aNumero(f[cPre]) : null,
          observacion: cObs >= 0 ? String(f[cObs] ?? '').trim() || null : null,
        });
      }
      if (!items.length) { error('El archivo no tiene artículos'); return; }
      await simular(items, archivo.name);
    } catch (err) {
      console.error('Error leyendo el archivo:', err);
      error('No se pudo leer el archivo. Debe ser un Excel (.xlsx)');
    }
  };

  const MUTACION_AJUSTE = `mutation($items: [AjusteInventarioInput!]!, $fecha: String, $simular: Boolean) {
    ajustarInventarioBodega(items: $items, fecha: $fecha, simular: $simular) {
      success message simulado sin_cambio errores
      ajustes { articulo_nombre unidad_medida es_nuevo tipo_movimiento stock_anterior stock_nuevo diferencia precio_unitario observacion }
    } }`;

  const simular = async (items, archivo) => {
    setAplicando(true);
    try {
      const r = await client.mutate(MUTACION_AJUSTE, { items, fecha: hoyBogota(), simular: true });
      setImporte({ items, archivo, resultado: r.ajustarInventarioBodega });
    } catch (err) {
      console.error('Error revisando el archivo:', err);
      error(err.message || 'No se pudo revisar el archivo');
    } finally {
      setAplicando(false);
    }
  };

  const aplicarImporte = async () => {
    if (!importe) return;
    setAplicando(true);
    try {
      const r = (await client.mutate(MUTACION_AJUSTE, { items: importe.items, fecha: hoyBogota(), simular: false })).ajustarInventarioBodega;
      if (r.success) { success(r.message); setImporte(null); cargar(); }
      else { error(r.message); setImporte({ ...importe, resultado: r }); }
    } catch (err) {
      console.error('Error aplicando el ajuste:', err);
      error(err.message || 'No se pudo aplicar el ajuste');
    } finally {
      setAplicando(false);
    }
  };

  // ---------- Kárdex de un artículo ----------
  const abrirKardex = async (nombre) => {
    setKardex({ nombre, cargando: true, articulo: null });
    try {
      const r = await consultaKardex(nombre);
      setKardex({ nombre, cargando: false, articulo: (r.bodegaKardex || [])[0] || null });
    } catch (err) {
      console.error('Error cargando el kárdex:', err);
      error('No se pudo cargar el kárdex');
      setKardex(null);
    }
  };

  const descargarKardex = () => {
    if (!kardex?.articulo) return;
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, hojaKardex([kardex.articulo]), 'Kárdex');
    const nombre = kardex.articulo.articulo_nombre.replace(/[^\w\- ]+/g, '').trim().slice(0, 40).replace(/\s+/g, '_');
    XLSX.writeFile(wb, `Kardex_${nombre}_${hoyBogota()}.xlsx`);
  };

  if (loading) {
    return (
      <div className="inventario-container">
        <div className="loading-spinner"><div className="spinner" /><p>Cargando inventario...</p></div>
      </div>
    );
  }

  const res = importe?.resultado;

  return (
    <div className="inventario-container">
      <div className="inventario-header">
        <div className="header-left">
          <h2>📋 Inventario de Bodega</h2>
          <p className="subtitle">Resumen agregado del stock (Entradas − Salidas)</p>
        </div>
        <div className="inv-acciones">
          <button className="btn-inv btn-inv-exportar" onClick={exportarExcel} disabled={exportando}>
            {exportando ? 'Exportando…' : '⬇ Exportar Excel'}
          </button>
          <button className="btn-inv btn-inv-importar" onClick={() => archivoRef.current?.click()} disabled={aplicando}>
            ⬆ Importar Excel
          </button>
          <input ref={archivoRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={leerArchivo} />
          <button className="btn-recargar" onClick={cargar} title="Refrescar">↻ Refrescar</button>
        </div>
      </div>

      {/* Estadísticas */}
      <div className="stats-grid">
        <div className={`stat-card ${filtro === 'todos' ? 'active' : ''}`} onClick={() => setFiltro('todos')}>
          <div className="stat-icon">📦</div>
          <div className="stat-content">
            <span className="stat-value">{stats.total}</span>
            <span className="stat-label">Todos los Artículos</span>
          </div>
        </div>
        <div className={`stat-card warning ${filtro === 'bajo' ? 'active' : ''}`} onClick={() => setFiltro('bajo')}>
          <div className="stat-icon">⚠️</div>
          <div className="stat-content">
            <span className="stat-value">{stats.bajo}</span>
            <span className="stat-label">Bajo Stock</span>
          </div>
        </div>
        <div className={`stat-card danger ${filtro === 'agotado' ? 'active' : ''}`} onClick={() => setFiltro('agotado')}>
          <div className="stat-icon">🚨</div>
          <div className="stat-content">
            <span className="stat-value">{stats.agotado}</span>
            <span className="stat-label">Agotados</span>
          </div>
        </div>
        <div className="stat-card success">
          <div className="stat-icon">💰</div>
          <div className="stat-content">
            <span className="stat-value">{formatMoney(stats.valor)}</span>
            <span className="stat-label">Valor Total Stock</span>
          </div>
        </div>
      </div>

      {/* Búsqueda */}
      <div className="filtros-section">
        <div className="busqueda-box">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Buscar artículo, proveedor o proyecto..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            className="search-input"
          />
        </div>
        {(filtro !== 'todos' || busqueda) && (
          <button className="btn-limpiar" onClick={() => { setFiltro('todos'); setBusqueda(''); }}>
            Limpiar filtros
          </button>
        )}
        <span className="contador">{inventarioFiltrado.length} de {inventario.length}</span>
      </div>

      {/* Tabla */}
      {inventarioFiltrado.length === 0 ? (
        <div className="empty-state">
          <span className="empty-icon">📦</span>
          <h3>{inventario.length === 0 ? 'No hay artículos en inventario' : 'Sin resultados'}</h3>
          <p>{inventario.length === 0 ? 'Registra entradas y salidas para ver el inventario' : 'Cambia los filtros o la búsqueda'}</p>
        </div>
      ) : (
        <div className="tabla-wrapper">
          <table className="inventario-table">
            <thead>
              <tr>
                <th>Artículo</th>
                <th className="th-right">Entradas</th>
                <th className="th-right">Salidas</th>
                <th className="th-right">Stock Actual</th>
                <th className="th-right">Precio Prom.</th>
                <th className="th-right">Valor Stock</th>
                <th>Última Entrada</th>
                <th>Último Proveedor</th>
                <th>Último Destino</th>
                <th className="th-center">Estado</th>
                <th className="th-center">Kárdex</th>
              </tr>
            </thead>
            <tbody>
              {inventarioFiltrado.map((item, idx) => {
                const estado = getEstadoStock(item.stock_actual);
                return (
                  <tr key={idx} className={`row-${estado}`}>
                    <td className="nombre">
                      <strong>{item.articulo_nombre}</strong>
                      <small className="movs">{item.num_movimientos} mov.</small>
                    </td>
                    <td className="td-right entradas">+{formatNum(item.total_entradas)} <small>{item.unidad_medida}</small></td>
                    <td className="td-right salidas">-{formatNum(item.total_salidas)} <small>{item.unidad_medida}</small></td>
                    <td className="td-right stock-actual">
                      <span className={`stock-badge ${estado}`}>
                        {formatNum(item.stock_actual)} {item.unidad_medida}
                      </span>
                    </td>
                    <td className="td-right precio">{formatMoney(item.precio_promedio)}</td>
                    <td className="td-right valor-total">{formatMoney(item.valor_total_stock)}</td>
                    <td className="fecha">{formatFecha(item.ultima_fecha_entrada)}</td>
                    <td className="proveedor">{item.ultimo_proveedor || '-'}</td>
                    <td className="proyecto">{item.ultimo_proyecto || '-'}</td>
                    <td className="td-center">
                      {estado === 'agotado' && <span className="badge-agotado">AGOTADO</span>}
                      {estado === 'bajo' && <span className="badge-bajo">BAJO</span>}
                      {estado === 'normal' && <span className="badge-normal">OK</span>}
                    </td>
                    <td className="td-center">
                      <button className="btn-kardex" onClick={() => abrirKardex(item.articulo_nombre)} title="Ver todos los movimientos del artículo">
                        Ver
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Kárdex del artículo */}
      {kardex && (
        <div className="inv-modal-fondo" onClick={() => setKardex(null)}>
          <div className="inv-modal inv-modal-ancho" onClick={(e) => e.stopPropagation()}>
            <div className="inv-modal-cabecera">
              <div>
                <h3>Kárdex · {kardex.nombre}</h3>
                {kardex.articulo && (
                  <p>
                    Entradas {formatNum(kardex.articulo.total_entradas)} · Salidas {formatNum(kardex.articulo.total_salidas)} ·
                    {' '}<strong>Saldo {formatNum(kardex.articulo.stock_actual)} {kardex.articulo.unidad_medida}</strong> ·
                    {' '}Valor {formatMoney(kardex.articulo.valor_stock)}
                  </p>
                )}
              </div>
              <div className="inv-acciones">
                {kardex.articulo && <button className="btn-inv btn-inv-exportar" onClick={descargarKardex}>⬇ Excel</button>}
                <button className="inv-cerrar" onClick={() => setKardex(null)}>✕</button>
              </div>
            </div>
            <div className="inv-modal-cuerpo">
              {kardex.cargando ? (
                <p className="inv-nota">Cargando movimientos…</p>
              ) : !kardex.articulo ? (
                <p className="inv-nota">Este artículo no tiene movimientos.</p>
              ) : (
                <table className="kardex-table">
                  <thead>
                    <tr>
                      <th>Fecha</th><th>Tipo</th><th>Detalle</th><th>Factura</th>
                      <th className="th-right">Entrada</th><th className="th-right">Salida</th><th className="th-right">Saldo</th>
                      <th className="th-right">Precio unit.</th><th className="th-right">Valor</th><th className="th-right">Saldo valorizado</th>
                      <th>Registró</th><th>Observaciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {kardex.articulo.movimientos.map(m => (
                      <tr key={m.id}>
                        <td className="fecha">{formatFecha(m.fecha)}</td>
                        <td><span className={`k-tipo ${TIPO_KARDEX[m.tipo]?.clase || ''}`}>{TIPO_KARDEX[m.tipo]?.texto || m.tipo}</span></td>
                        <td>{m.detalle || '-'}</td>
                        <td>{m.numero_factura || '-'}</td>
                        <td className="td-right entradas">{m.entrada ? `+${formatNum(m.entrada)}` : ''}</td>
                        <td className="td-right salidas">{m.salida ? `-${formatNum(m.salida)}` : ''}</td>
                        <td className="td-right"><strong>{formatNum(m.saldo)}</strong></td>
                        <td className="td-right">{formatMoney(m.precio_unitario)}</td>
                        <td className="td-right">{formatMoney(m.valor_movimiento)}</td>
                        <td className="td-right">{formatMoney(m.saldo_valor)}</td>
                        <td>{m.usuario || '-'}</td>
                        <td className="k-obs">{m.observaciones || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Vista previa del importe */}
      {importe && res && (
        <div className="inv-modal-fondo">
          <div className="inv-modal" onClick={(e) => e.stopPropagation()}>
            <div className="inv-modal-cabecera">
              <div>
                <h3>Importar inventario</h3>
                <p>{importe.archivo} · {importe.items.length} artículo(s) leídos</p>
              </div>
              <button className="inv-cerrar" onClick={() => setImporte(null)} disabled={aplicando}>✕</button>
            </div>
            <div className="inv-modal-cuerpo">
              {res.errores.length > 0 ? (
                <>
                  <p className="inv-nota inv-nota-error">El archivo tiene errores. Corrígelos y vuelve a importarlo. No se aplicó ningún cambio.</p>
                  <ul className="inv-errores">{res.errores.map((t, i) => <li key={i}>{t}</li>)}</ul>
                </>
              ) : res.ajustes.length === 0 ? (
                <p className="inv-nota">El conteo del archivo coincide con el sistema en los {res.sin_cambio} artículo(s). No hay nada que ajustar.</p>
              ) : (
                <>
                  <p className="inv-nota">
                    Se registrará un movimiento de ajuste en el kárdex de <strong>{res.ajustes.length}</strong> artículo(s).
                    {' '}{res.sin_cambio} artículo(s) no cambian. Nada se aplica hasta que confirmes.
                  </p>
                  <table className="kardex-table">
                    <thead>
                      <tr>
                        <th>Artículo</th><th className="th-right">Sistema</th><th className="th-right">Físico</th>
                        <th className="th-right">Diferencia</th><th>Movimiento</th><th className="th-right">Precio</th><th>Observación</th>
                      </tr>
                    </thead>
                    <tbody>
                      {res.ajustes.map((a, i) => (
                        <tr key={i}>
                          <td><strong>{a.articulo_nombre}</strong>{a.es_nuevo && <span className="k-tipo k-ajuste" style={{ marginLeft: 6 }}>Nuevo</span>}</td>
                          <td className="td-right">{formatNum(a.stock_anterior)}</td>
                          <td className="td-right"><strong>{formatNum(a.stock_nuevo)}</strong></td>
                          <td className={`td-right ${a.diferencia > 0 ? 'entradas' : 'salidas'}`}>{a.diferencia > 0 ? '+' : ''}{formatNum(a.diferencia)}</td>
                          <td><span className={`k-tipo ${a.tipo_movimiento === 'entrada' ? 'k-entrada' : 'k-salida'}`}>Ajuste de {a.tipo_movimiento}</span></td>
                          <td className="td-right">{formatMoney(a.precio_unitario)}</td>
                          <td className="k-obs">{a.observacion || ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>
            <div className="inv-modal-pie">
              <button className="btn-recargar" onClick={() => setImporte(null)} disabled={aplicando}>
                {res.errores.length || !res.ajustes.length ? 'Cerrar' : 'Cancelar'}
              </button>
              {res.errores.length === 0 && res.ajustes.length > 0 && (
                <button className="btn-inv btn-inv-importar" onClick={aplicarImporte} disabled={aplicando}>
                  {aplicando ? 'Aplicando…' : `Aplicar ${res.ajustes.length} ajuste(s)`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventarioBodega;
